import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Sparkles, TrendingUp, Wrench, ShieldCheck, Palette, Zap, Plug, X } from "lucide-react";

const CAT = {
  new: { icon: Sparkles, color: "#EAB308", fr: "NOUVEAU", en: "NEW" },
  improvement: { icon: TrendingUp, color: "#4c9dff", fr: "AMÉLIORÉ", en: "IMPROVED" },
  fix: { icon: Wrench, color: "#22c55e", fr: "CORRIGÉ", en: "FIXED" },
  security: { icon: ShieldCheck, color: "#dc143c", fr: "SÉCURITÉ", en: "SECURITY" },
  design: { icon: Palette, color: "#EAB308", fr: "DESIGN", en: "DESIGN" },
  automation: { icon: Zap, color: "#4c9dff", fr: "AUTOMATISATION", en: "AUTOMATION" },
  integration: { icon: Plug, color: "#22c55e", fr: "INTÉGRATION", en: "INTEGRATION" },
};

export function ReleasePopup() {
  const { lang } = useI18n();
  const [rel, setRel] = useState(null);
  const L = (fr, en) => (lang === "en" ? en : fr);

  useEffect(() => {
    api.get("/releases/unseen").then(({ data }) => { if (data.release) setRel(data.release); }).catch(() => {});
  }, []);

  const close = async () => {
    if (rel) { try { await api.post(`/releases/${rel.id}/acknowledge`); } catch (e) {} }
    setRel(null);
  };

  if (!rel) return null;
  const title = lang === "en" ? rel.title_en || rel.title_fr : rel.title_fr;
  const desc = lang === "en" ? rel.description_en || rel.description_fr : rel.description_fr;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4" data-testid="release-popup">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={rel.mandatory_update ? undefined : close} />
      <div className="relative w-full max-w-[620px] max-h-[85vh] overflow-y-auto rounded-xl border border-[#EAB308]/55 bg-[#15161b] shadow-[0_28px_80px_rgba(0,0,0,0.55),0_0_30px_rgba(234,179,8,0.08)]">
        {!rel.mandatory_update && (
          <button onClick={close} data-testid="release-popup-close" className="absolute top-4 right-4 text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        )}
        <div className="p-7">
          <div className="flex items-center gap-3">
            <span className="text-[11px] font-black uppercase tracking-[0.2em] text-white/50">{L("Nouvelle version", "New version")}</span>
            <span className="inline-flex items-center gap-1.5 rounded px-2.5 py-1 text-[11px] font-black text-black bg-[#EAB308]">v{rel.version}</span>
          </div>
          <h2 className="mt-2 text-2xl font-black">
            <span className="pg-pro">PRO</span><span className="pg-gere">GÈRE</span> <span className="progere-brand-module">CORE</span>
          </h2>
          <p className="mt-2 text-[15px] font-semibold text-white">{title}</p>
          {desc && <p className="mt-1 text-sm text-white/60">{desc}</p>}

          <div className="mt-6 space-y-2.5">
            {(rel.items || []).map((it, i) => {
              const c = CAT[it.category] || CAT.new;
              const Icon = c.icon;
              const itTitle = lang === "en" ? it.title_en || it.title_fr : it.title_fr;
              return (
                <div key={i} className="flex items-start gap-3 rounded-lg border border-white/10 bg-[#111217] px-3.5 py-3">
                  <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md" style={{ background: `${c.color}18` }}>
                    <Icon className="w-4 h-4" style={{ color: c.color }} />
                  </div>
                  <div className="min-w-0">
                    <span className="inline-block rounded px-1.5 py-0.5 text-[8px] font-black tracking-wider" style={{ color: c.color, background: `${c.color}18` }}>{L(c.fr, c.en)}</span>
                    <p className="mt-1 text-sm text-white/85">{itTitle}</p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-7 flex items-center justify-between gap-3">
            <a href="/nouveautes" className="text-sm font-semibold text-[#EAB308] hover:underline" data-testid="release-see-all">{L("Voir toutes les nouveautés", "See all release notes")}</a>
            <button onClick={close} data-testid="release-continue" className="rounded-lg bg-[#EAB308] px-5 py-2.5 text-sm font-bold text-black hover:bg-[#ffd45c] transition-colors">
              {rel.mandatory_update ? L("Mettre à jour maintenant", "Update now") : L("Continuer", "Continue")}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
